/**
 * Auth-Context (ADR-003 v2: SWA Built-in Auth / Entra).
 * Der Auth-STATUS kommt aus /.auth/me (First-Party-SWA-Cookie, nicht vom
 * gestrippten Backend-Cookie — siehe L-011). login/logout sind SSO-Redirects.
 * Die AUTORISIERUNG (Allow-List) macht das Backend; ein angemeldeter, aber nicht
 * berechtigter Nutzer ist hier `authenticated`, bekommt aber 403 auf den Daten.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/client';
import type { ClientPrincipal } from '../api/client';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  principal: ClientPrincipal | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [principal, setPrincipal] = useState<ClientPrincipal | null>(null);

  const probe = useCallback(async () => {
    try {
      const p = await api.getAuthMe();
      setPrincipal(p);
      setStatus(p ? 'authenticated' : 'unauthenticated');
    } catch {
      setPrincipal(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void probe();
  }, [probe]);

  // login/logout verlassen die Seite (Redirect zu Entra) -> kein setState nötig.
  const login = useCallback(() => api.login(), []);
  const logout = useCallback(() => api.logout(), []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, principal, login, logout }),
    [status, principal, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden');
  return ctx;
}
